import { useState, useRef } from "react";
import { storyblokEditable } from "@storyblok/react";

import ResourceCard from "./ResourceCard";
import ContactUsForm from "./ContactUsForm";

const resolveStoryblokLink = (link) => {
  if (!link) return "#";
  if (link.linktype === "story") {
    const slug = link.cached_url || link.story?.full_slug;
    return slug ? `/${slug}` : "#";
  }
  if (link.linktype === "asset") return link.url || "#";
  return link.url || link.cached_url || "#";
};

const ResourcesCards = ({ blok }) => {
  const [showGate, setShowGate] = useState(false);
  // Track which card's video URL we're gating so multiple gated cards work correctly
  const [pendingVideoUrl, setPendingVideoUrl] = useState(null);
  const gateSectionRef = useRef(null);

  const backgroundImage = blok.background_image?.filename
    ? `url(${blok.background_image.filename})`
    : "none";

  const cards = blok.cards || [];
  const firstRowCards = cards.slice(0, 3);
  const secondRowCards = cards.slice(3, 6);

  const reverseSecondRow = !!blok.reverse_card_layout;
  const bottomButtonHref = resolveStoryblokLink(blok.button_url);

  // Receives the specific card's video URL
  const handleGateClick = (videoUrl) => {
    setPendingVideoUrl(videoUrl || null);
    setShowGate(true);

    setTimeout(() => {
      gateSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  };

  // Called by ContactUsForm after a successful submission
  const handleSuccess = () => {
    setShowGate(false);

    if (pendingVideoUrl) {
      window.open(pendingVideoUrl, "_blank", "noopener,noreferrer");
    }

    setPendingVideoUrl(null);
  };

  const renderCard = (card) => (
    <ResourceCard
      blok={card}
      key={card._uid}
      onGateClick={handleGateClick}
    />
  );

  return (
    <section
      {...storyblokEditable(blok)}
      className="bg-cover bg-center py-12 px-4 md:px-12"
      style={{ backgroundImage }}
    >
      {blok.title && (
        <h2 className="text-center text-3xl font-semibold mb-10">
          {blok.title}
        </h2>
      )}

      {/* Mobile / Tablet */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-6xl mx-auto lg:hidden">
        {cards.map(renderCard)}
      </div>

      {/* Desktop */}
      <div className="hidden lg:flex lg:flex-col lg:gap-6 lg:max-w-6xl lg:mx-auto">
        <div className="grid gap-6 lg:grid-cols-[33%_24%_43%]">
          {firstRowCards.map(renderCard)}
        </div>

        <div
          className={
            reverseSecondRow
              ? "grid gap-6 lg:grid-cols-[43%_24%_33%]"
              : "grid gap-6 lg:grid-cols-[33%_24%_43%]"
          }
        >
          {secondRowCards.map(renderCard)}
        </div>
      </div>

      {/* Bottom Button */}
      {blok.button_text && (
        <div className="flex justify-center mt-12">
          <a
            href={bottomButtonHref}
            className="flex items-center gap-2 bg-[#1371DC] text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-blue-700 transition"
          >
            {blok.button_icon?.filename && (
              <img
                src={blok.button_icon.filename}
                alt=""
                className="w-[19px] h-[14px] object-contain"
              />
            )}
            <span>{blok.button_text}</span>
          </a>
        </div>
      )}

      {/* Gated Form */}
      {showGate && (
        <div
          ref={gateSectionRef}
          className="mt-12 max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8"
        >
          <h3 className="text-2xl font-semibold text-center mb-6">
            Fill out the form to access this video
          </h3>

          {blok.contact_form?.[0] && (
            <ContactUsForm
              blok={blok.contact_form[0]}
              onSuccess={handleSuccess}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default ResourcesCards;